// DashboardPage — aggregate counters from GET /api/v1/dashboard.
// Shows total domains, scans last 30d, grade distribution, and complaint count.

import { useQuery } from "@tanstack/react-query";
import { Globe, BarChart2, AlertTriangle, ScanLine, Shield } from "lucide-react";
import { fetchDashboard, type DashboardData } from "../api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@avagency/frontend/components/ui/card";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";
import { Footer } from "../components/Footer";

// Grade config — rainbow preserved per brief.
// textClass, barColor, badgeClass must be full strings for Tailwind JIT.
const GRADE_CONFIG: Record<string, {
  textClass: string;
  barColor: string;
  badgeClass: string;
  label: string;
}> = {
  A: {
    textClass: "text-green-400",
    barColor: "#9be3b1",
    badgeClass: "grade-badge-A",
    label: "Excellent",
  },
  B: {
    textClass: "text-lime-400",
    barColor: "#84cc16",
    badgeClass: "grade-badge-B",
    label: "Good",
  },
  C: {
    textClass: "text-yellow-400",
    barColor: "#facc15",
    badgeClass: "grade-badge-C",
    label: "Fair",
  },
  D: {
    textClass: "text-orange-400",
    barColor: "#fb923c",
    badgeClass: "grade-badge-D",
    label: "Poor",
  },
  F: {
    textClass: "text-red-400",
    barColor: "#ff8a8a",
    badgeClass: "grade-badge-F",
    label: "Critical",
  },
};

interface StatCardProps {
  title: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactNode;
}

function StatCard({ title, value, sub, icon }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-3 pt-5 px-5">
        <CardTitle
          className="text-sm font-medium"
          style={{ color: "var(--yg-silver-2)", letterSpacing: "-0.01em" }}
        >
          {title}
        </CardTitle>
        <div className="yg-stat-icon" aria-hidden="true">{icon}</div>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <div className="yg-stat-value">{value}</div>
        {sub && (
          <p
            className="mt-2 text-xs"
            style={{ color: "var(--yg-silver-3)", fontFamily: "var(--yg-font-mono)", letterSpacing: "0.04em" }}
          >
            {sub}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function GradeBar({ dist }: { dist: Record<string, number> }) {
  const grades = ["A", "B", "C", "D", "F"];
  const total = grades.reduce((s, g) => s + (dist[g] ?? 0), 0);

  if (total === 0) {
    return (
      <div className="yg-empty-state" style={{ padding: "32px 24px" }}>
        <div className="yg-empty-icon" aria-hidden="true">
          <BarChart2 className="h-5 w-5" />
        </div>
        <div className="yg-empty-title" style={{ fontSize: "15px" }}>
          No graded scans yet
        </div>
        <p className="yg-empty-body" style={{ fontSize: "13px" }}>
          Grade distribution appears here once compliance scans complete.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {grades.map((g) => {
        const n = dist[g] ?? 0;
        if (n === 0) return null;
        const pct = Math.round((n / total) * 100);
        const cfg = GRADE_CONFIG[g];
        return (
          <div key={g} className="flex items-center gap-3 text-sm">
            <span
              className={`w-5 text-xs font-bold font-mono ${cfg?.textClass ?? ""}`}
              title={cfg?.label}
            >
              {g}
            </span>
            <div className="yg-grade-bar-track flex-1">
              <div
                className="yg-grade-bar-fill"
                style={{ width: `${pct}%`, background: cfg?.barColor ?? "#60a5fa" }}
                role="meter"
                aria-valuenow={n}
                aria-valuemin={0}
                aria-valuemax={total}
                aria-label={`Grade ${g}: ${n} domain${n !== 1 ? "s" : ""}`}
              />
            </div>
            <div className="flex items-center gap-2 w-20 justify-end">
              <span
                className="text-xs"
                style={{ color: "var(--yg-silver-3)", fontFamily: "var(--yg-font-mono)" }}
              >
                {pct}%
              </span>
              <span
                className="text-xs font-medium w-4 text-right"
                style={{ color: "var(--yg-silver-2)" }}
              >
                {n}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DashboardEmpty() {
  return (
    <div className="yg-empty-state">
      <div className="yg-empty-icon" aria-hidden="true">
        <Shield className="h-6 w-6" />
      </div>
      <div className="yg-empty-title">Connect your sending domain</div>
      <p className="yg-empty-body">
        Link a Mailchimp or Brevo account to start auditing SPF, DKIM, DMARC records
        and tracking complaint rates across your sending domains.
      </p>
    </div>
  );
}

function CounterCards({ d }: { d: DashboardData }) {
  const hasData =
    d.total_domains > 0 || d.scans_last_30d > 0 || d.total_complaints > 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Domains"
          value={d.total_domains}
          sub="Registered sending domains"
          icon={<Globe className="h-4 w-4" />}
        />
        <StatCard
          title="Scans (30d)"
          value={d.scans_last_30d}
          sub="Compliance scans last 30 days"
          icon={<ScanLine className="h-4 w-4" />}
        />
        <StatCard
          title="Complaints"
          value={d.total_complaints}
          sub="Total complaint events tracked"
          icon={<AlertTriangle className="h-4 w-4" />}
        />
      </div>

      {!hasData && <DashboardEmpty />}

      {hasData && (
        <Card>
          <CardHeader className="pb-3 pt-5 px-5">
            <CardTitle
              className="flex items-center gap-2 text-sm font-medium"
              style={{ color: "var(--yg-silver-2)" }}
            >
              <BarChart2 className="h-4 w-4" aria-hidden="true" />
              Grade Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <GradeBar dist={d.grade_distribution} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function DashboardPage() {
  const q = useQuery<DashboardData>({
    queryKey: ["yahoogle", "dashboard"],
    queryFn: fetchDashboard,
  });

  return (
    <div className="space-y-6">
      <div>
        <span className="yg-eyebrow">Overview</span>
        <h1 className="yg-page-heading text-2xl">Dashboard</h1>
        <p className="yg-page-sub">
          Email compliance — SPF, DKIM, DMARC, BIMI, and complaint rates.
        </p>
      </div>

      {q.isLoading && <LoadingState rows={4} />}
      {q.error && <ErrorState error={q.error} retry={() => q.refetch()} />}
      {q.data && <CounterCards d={q.data} />}

      <Footer />
    </div>
  );
}
